import {
  CalendarOutlined,
  ShoppingOutlined,
  WalletOutlined,
  WifiOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, Col, Flex, Row, Skeleton, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { fetchBalance } from '@/features/dashboard/balanceApi'
import { fetchUsage } from '@/features/usage/usageApi'
import { UsageChart } from '@/features/usage/UsageChart'
import { queryKeys } from '@/shared/api/queryKeys'
import { useAuth } from '@/shared/auth/useAuth'
import { formatBalanceBytes, formatDate, formatDuration } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { LoadError } from '@/shared/ui/LoadError'
import { StatCard } from '@/shared/ui/StatCard'
import { StatusBanner } from '@/shared/ui/StatusBanner'

/**
 * Amaldagi paketdan "qolgan" ni ko'rsatiladigan matnga aylantiradi.
 *
 * Reja ikki xil o'lchamda bo'lishi mumkin (`BillingKind`): hajm bilan
 * (`quotaBytes`) yoki vaqt bilan (`quotaSeconds`). Ikkalasi ham `null`
 * bo'lsa — cheksiz (`UNLIMITED`). Ikkalasi HAM bo'lishi (masalan hotspot
 * vaucherida hajm VA vaqt chegarasi) nazariy jihatdan mumkin — bunda
 * ikkalasi ham ko'rsatiladi, chunki qaysi biri oldin tugashi oldindan
 * aniq emas.
 */
function remainingLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  grant: { quotaBytes: number | null; remainingBytes: number | null; quotaSeconds: number | null; remainingSeconds: number | null },
): string {
  const parts: string[] = []
  if (grant.quotaBytes !== null) parts.push(formatBalanceBytes(grant.remainingBytes))
  if (grant.quotaSeconds !== null) parts.push(formatDuration(grant.remainingSeconds))
  if (parts.length === 0) return t('common.unlimited')
  return parts.join(' · ')
}

/**
 * BARCHA amaldagi paketlarning yig'indisi.
 *
 * ⚠️ `bytes: null` — CHEKSIZ, `0` esa TUGAGAN. Ikkalasini bir xil
 * ko'rsatish abonentni "paketim bor" deb adashtirardi.
 *
 * ⚠️ Paketlar soni ham yoziladi ("2 ta paket"): usiz abonent
 * yig'indi qayerdan kelganini bilmaydi va raqamni xato deb
 * hisoblaydi — aynan shu shikoyat kelgan edi.
 */
function totalLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  total: { bytes: number | null; seconds: number | null; packets: number },
): string {
  const parts: string[] = []
  if (total.bytes !== null) parts.push(formatBalanceBytes(total.bytes))
  if (total.seconds !== null && total.seconds > 0) parts.push(formatDuration(total.seconds))
  /*
    ⚠️ PAKETLAR SONI ENDI BU YERDA EMAS — u `StatCard` ning `hint`
    maydoniga chiqadi.

    Ilgari qiymat "118.8 GB (5 ta paket)" bo'lardi va telefon ekranida
    28px shriftda u so'z O'RTASIDAN bo'linardi: "paket / lar". Katta
    raqam kartaning butun ma'nosi edi, u esa o'qib bo'lmas holga
    kelardi.
  */
  return parts.length === 0 ? t('common.unlimited') : parts.join(' · ')
}

export function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()

  /*
    ⚠️ Avtoyangilanish — kabinetning ASOSIY savoli shu sahifada.

    Abonent to'lov qilgach yoki paket sotib olgach aynan shu ekranga
    qaraydi va o'zgarishni kutadi. Yangilanishsiz u eski raqamni
    ko'rardi va "pulim tushmadi" degan xulosaga kelardi — aslida
    tushgan, faqat sahifa qotib qolgan. Qo'lda F5 bosish kerakligini
    hech kim aytmaydi.

    30 soniya — qurilma batareyasi bilan murosa: to'lov soniyalar
    ichida emas, o'nlab soniyalarda tasdiqlanadi.
  */
  const balanceQuery = useQuery({
    queryKey: queryKeys.balance,
    queryFn: fetchBalance,
    refetchInterval: 30_000,
  })
  const usageQuery = useQuery({
    queryKey: queryKeys.usage,
    queryFn: () => fetchUsage(),
    refetchInterval: 30_000,
  })

  if (!user) return null

  const grant = balanceQuery.data?.activeGrant ?? null
  const total = balanceQuery.data?.totalRemaining ?? null

  /**
   * Abonentda amaldagi paket BORMI.
   *
   * ⚠️ `total` obyektining o'zi bu savolga javob bermaydi: backend
   * paket bo'lmasa ham `{bytes:0, seconds:0, packets:0}` qaytaradi va
   * JS da har obyekt "truthy". Javob faqat `packets` da.
   */
  const hasPackage = (total?.packets ?? 0) > 0 || grant !== null

  /**
   * Balans HAQIQATAN o'qildimi.
   *
   * ⚠️ `!isPending` bilan BIR XIL EMAS: so'rov yiqilganda ham `isPending`
   * yolg'on bo'ladi va `data` yo'q. O'shanda kartalar "Amaldagi paket
   * yo'q" deb yozardi — bu ISHONCHLI javob bo'lib o'qiladi, holbuki
   * hech narsa o'lchanmagan. Abonent shu yozuvga qarab ikkinchi marta
   * paket sotib olishi mumkin edi.
   *
   * ⚠️ `isSuccess` ham emas, `data !== undefined`: 30 soniyalik
   * avtoyangilanishda bitta muvaffaqiyatsiz so'rov `isSuccess` ni
   * yiqitadi, lekin OLDINGI qiymat hali qo'lda turadi va uni ekrandan
   * olib tashlash foydali ma'lumotni yo'qotish bo'lardi. Yiqilganini
   * yuqoridagi banner aytadi.
   */
  const balanceKnown = balanceQuery.data !== undefined

  const needsAttention =
    user.state !== 'ACTIVE' || (balanceQuery.data !== undefined && !hasPackage)

  // Kvota tugash arafasida — hajm bo'yicha 1 GB dan kam yoki vaqt bo'yicha
  // 1 soatdan kam qolganda ogohlantirish rangi
  const lowQuota =
    grant !== null &&
    ((grant.remainingBytes !== null && grant.remainingBytes < 1024 ** 3) ||
      (grant.remainingSeconds !== null && grant.remainingSeconds < 3600))

  return (
    <Flex vertical gap={20}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        {t('dashboard.greeting', { name: user.displayName })}
      </Typography.Title>

      {/*
        ⚠️ `hasPackage` BALANS KELGANDAN KEYIN beriladi, oldin `undefined`.

        Yuklanish paytida uni `false` deb yuborish "paketingiz yo'q" degan
        YOLG'ON sababni ko'rsatardi — X-SIB-001 ning aynan o'zi, faqat
        boshqa shaklda (`StatusBanner` javadoc'i).
      */}
      <StatusBanner
        state={user.state}
        hasPackage={balanceQuery.data === undefined ? undefined : hasPackage}
      />

      {needsAttention && (
        <Button type="primary" size="large" icon={<ShoppingOutlined />} onClick={() => navigate('/plans')}>
          {t('dashboard.buyPackageCta')}
        </Button>
      )}

      {balanceQuery.isError && (
        <LoadError
          titleKey="dashboard.loadFailed"
          error={balanceQuery.error}
          onRetry={() => void balanceQuery.refetch()}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={12} md={8}>
          {balanceQuery.isPending ? (
            <Card><Skeleton active paragraph={{ rows: 1 }} /></Card>
          ) : (
            <StatCard
              icon={<WalletOutlined />}
              label={t('dashboard.moneyLabel')}
              // Balans o'qilmagan bo'lsa chiziqcha — "0 so'm" EMAS
              value={balanceKnown ? formatMoneyValue(balanceQuery.data?.money) : t('common.dash')}
            />
          )}
        </Col>
        <Col xs={12} md={8}>
          {balanceQuery.isPending ? (
            <Card><Skeleton active paragraph={{ rows: 1 }} /></Card>
          ) : (
            <StatCard
              icon={<WifiOutlined />}
              label={t('dashboard.trafficLabel')}
              // ⚠️ BARCHA paketlar yig'indisi, bittasi EMAS.
              //
              // Ilgari bu yerda `activeGrant` (hozir sarflanayotgan
              // BITTA paket) turardi. Abonent esa bu raqamni umumiy
              // qoldiq deb o'qiydi: 3 x 5 GB olgan odam 5 GB ko'rib
              // "paketim qayerda?" deb so'rardi.
              //
              // Zaxira sifatida eski qiymat qoladi — eski backend
              // bilan ishlaganda ekran bo'sh qolmasin.
              // ⚠️ `total.packets > 0` — `total` NING O'ZI emas.
              //
              // Backend har doim `TotalRemaining` obyekt qaytaradi:
              // paket bo'lmasa ham `{bytes:0, seconds:0, packets:0}`.
              // Obyekt esa JS da doim "truthy" — ya'ni `total ? …`
              // tekshiruvi HECH QACHON yolg'on bo'lmaydi va "paket
              // yo'q" tarmog'i o'lik kod bo'lib qolardi: abonent
              // paketsiz holatda "0 B" ko'rardi.
              //
              // ⚠️ `balanceKnown` BIRINCHI shart: "paket yo'q" degan xulosa
              // faqat javob KELGANDA chiqariladi (yuqoridagi izoh).
              value={
                !balanceKnown
                  ? t('common.dash')
                  : total && total.packets > 0
                    ? totalLabel(t, total)
                    : grant
                      ? remainingLabel(t, grant)
                      : t('dashboard.noActivePackage')
              }
              // ⚠️ Paketlar soni QIYMATDA emas, IZOHDA: sabab `totalLabel` da.
              // Usiz abonent yig'indi qayerdan kelganini bilmaydi va raqamni
              // xato deb hisoblaydi — aynan shu shikoyat kelgan edi.
              hint={
                balanceKnown && total && total.packets > 1
                  ? t('dashboard.packets', { count: total.packets })
                  : undefined
              }
              // Ma'lumot yo'q — sariq rang ham yo'q: ogohlantirish O'ZI
              // "paketingiz tugayapti" degan xulosa, uni chiqarish uchun
              // avval o'lchash kerak.
              tone={
                !balanceKnown ? 'default' : !hasPackage ? 'warning' : lowQuota ? 'warning' : 'default'
              }
            />
          )}
        </Col>
        <Col xs={24} md={8}>
          {balanceQuery.isPending ? (
            <Card><Skeleton active paragraph={{ rows: 1 }} /></Card>
          ) : (
            <StatCard
              icon={<CalendarOutlined />}
              label={t('dashboard.expiresTitle')}
              // `expiresAt` null — paket MUDDATSIZ, sana yo'q. Chiziqcha
              // ko'rsatiladi, "Invalid Date" emas.
              value={grant?.expiresAt ? formatDate(grant.expiresAt) : t('common.dash')}
              hint={t(`deliveryMethod.${user.kind}`)}
            />
          )}
        </Col>
      </Row>

      <Card
        title={t('dashboard.usageChartTitle')}
        extra={
          <Button type="link" onClick={() => navigate('/usage')} style={{ paddingInlineEnd: 0 }}>
            {t('common.viewAll')}
          </Button>
        }
      >
        {usageQuery.isPending ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : usageQuery.isError ? (
          <LoadError
            titleKey="usage.loadFailed"
            error={usageQuery.error}
            onRetry={() => void usageQuery.refetch()}
          />
        ) : (
          <UsageChart data={usageQuery.data} compact />
        )}
      </Card>
    </Flex>
  )
}
